import type { TFunction } from 'i18next';
import { Typography } from '@heroui/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { cn } from '@/utils/cnUtils';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    isFlaggedRow,
    type ImportRow,
    type ImportRowEditHandler,
    type ImportRowExclusionHandlers,
} from '../../importRows';
import { GroupMarker } from '../groupMarker';
import { MatchChip } from '../matchChip';
import { NoteCell } from '../noteCell';
import { RowEditButton } from '../rowEdit';
import { RowExclusionButton } from '../rowExclusion';
import { StatusChip } from '../statusChip';
import {
    formatQuantity,
    IMPORT_TABLE_ACTIONS_WIDTH_PX,
    IMPORT_TABLE_CODE_WIDTH_PX,
    IMPORT_TABLE_COLUMN,
    IMPORT_TABLE_NOTE_MIN_WIDTH_PX,
} from './importTableMeta';

const moneyOf = (tetri: number | null): string =>
    tetri === null ? '' : formatGel(tetri, { withSymbol: false });

/** An excluded row's text reads in the muted colour; its status chip says why. */
const textClass = (row: ImportRow, className: string): string =>
    cn(className, row.isExcluded && 'text-muted');

/** The same for a Typography cell: muted on an excluded row. */
const textColor = (row: ImportRow): 'muted' | 'default' =>
    row.isExcluded ? 'muted' : 'default';

/**
 * The Products table's columns: Excel row, status, name, SKU with its group
 * marker, barcode, lot number, attributes, quantity, cost, sale price, match,
 * note, and the row's actions. Every other cell is read-only text or a chip:
 * no form control, no request. Long texts truncate and carry the full text as
 * their title; the note's sentence opens the whole note in a popover
 * (`NoteCell`). An excluded row reads muted.
 *
 * The last column holds the row's two controls, icon buttons with a
 * tooltip: **Edit**, which opens the row in the drawer as the row's press
 * does, and **Exclude / Include**, which writes the store's exclusions and
 * sends nothing. A flagged row's note ends with a **Fix** link that opens the
 * drawer too (`isFlaggedRow`). While the import runs they wait
 * (`RowExclusionLockContext`, which the table provides, so the columns do
 * not change with it).
 *
 * Virtualized mode lays the columns out with flexbox, so name, attributes and
 * note share the slack by their `fr` widths and keep a minimum; barcode and
 * lot number keep a fixed width that holds a 15-digit IMEI.
 */
export const buildImportTableColumns = (
    t: TFunction,
    {
        onEditRow,
        onExcludeRow,
        onIncludeRow,
    }: ImportRowEditHandler & ImportRowExclusionHandlers,
): DataGridColumn<ImportRow>[] => [
    {
        id: IMPORT_TABLE_COLUMN.row,
        header: t('catalogImport.products.columns.row'),
        width: 60,
        minWidth: 60,
        cell: (row) => (
            <span
                className="text-sm tabular-nums text-muted"
                {...getNodeTestIdProp(
                    `catalog-import-row-number-${row.rowNumber}`,
                )}
            >
                {row.rowNumber}
            </span>
        ),
    },
    {
        id: IMPORT_TABLE_COLUMN.status,
        header: t('catalogImport.products.columns.status'),
        width: 140,
        minWidth: 140,
        cell: (row) => (
            <StatusChip rowNumber={row.rowNumber} status={row.status} />
        ),
    },
    {
        id: IMPORT_TABLE_COLUMN.name,
        header: t('catalogImport.products.columns.name'),
        isRowHeader: true,
        width: '1fr',
        minWidth: 110,
        cell: (row) => (
            <span
                className={textClass(row, 'truncate text-sm font-medium')}
                title={row.name}
                {...getNodeTestIdProp(
                    `catalog-import-row-name-${row.rowNumber}`,
                )}
            >
                {row.name}
            </span>
        ),
    },
    {
        id: IMPORT_TABLE_COLUMN.sku,
        header: t('catalogImport.products.columns.sku'),
        width: 176,
        minWidth: 176,
        cell: (row) => (
            <span className="flex min-w-0 items-center gap-1.5">
                <span
                    className="truncate text-sm text-muted"
                    title={row.sku}
                    {...getNodeTestIdProp(
                        `catalog-import-row-sku-${row.rowNumber}`,
                    )}
                >
                    {row.sku}
                </span>
                {row.groupSize === null ? null : (
                    <GroupMarker
                        groupSize={row.groupSize}
                        rowNumber={row.rowNumber}
                        sku={row.sku}
                    />
                )}
            </span>
        ),
    },
    {
        id: IMPORT_TABLE_COLUMN.barcode,
        header: t('catalogImport.products.columns.barcode'),
        width: IMPORT_TABLE_CODE_WIDTH_PX,
        minWidth: IMPORT_TABLE_CODE_WIDTH_PX,
        cell: (row) => (
            <Typography
                truncate
                className="tabular-nums"
                color={textColor(row)}
                title={row.barcode}
                type="body-sm"
                {...getNodeTestIdProp(
                    `catalog-import-row-barcode-${row.rowNumber}`,
                )}
            >
                {row.barcode}
            </Typography>
        ),
    },
    {
        id: IMPORT_TABLE_COLUMN.lotNumber,
        header: t('catalogImport.products.columns.lotNumber'),
        width: IMPORT_TABLE_CODE_WIDTH_PX,
        minWidth: IMPORT_TABLE_CODE_WIDTH_PX,
        cell: (row) => (
            <Typography
                truncate
                className="tabular-nums"
                color={textColor(row)}
                title={row.lotNumber}
                type="body-sm"
                {...getNodeTestIdProp(
                    `catalog-import-row-lot-number-${row.rowNumber}`,
                )}
            >
                {row.lotNumber}
            </Typography>
        ),
    },
    {
        id: IMPORT_TABLE_COLUMN.attributes,
        header: t('catalogImport.products.columns.attributes'),
        width: '0.8fr',
        minWidth: 84,
        cell: (row) => (
            <span
                className={textClass(row, 'truncate text-sm')}
                title={row.attributes}
                {...getNodeTestIdProp(
                    `catalog-import-row-attributes-${row.rowNumber}`,
                )}
            >
                {row.attributes}
            </span>
        ),
    },
    {
        id: IMPORT_TABLE_COLUMN.quantity,
        header: t('catalogImport.products.columns.quantity'),
        align: 'end',
        width: 76,
        minWidth: 76,
        cell: (row) => (
            <span
                className={textClass(row, 'text-sm tabular-nums')}
                {...getNodeTestIdProp(
                    `catalog-import-row-quantity-${row.rowNumber}`,
                )}
            >
                {row.quantity === null ? '' : formatQuantity(row.quantity)}
            </span>
        ),
    },
    {
        id: IMPORT_TABLE_COLUMN.cost,
        header: t('catalogImport.products.columns.cost'),
        align: 'end',
        width: 100,
        minWidth: 100,
        cell: (row) => (
            <span
                className={textClass(row, 'text-sm tabular-nums')}
                {...getNodeTestIdProp(
                    `catalog-import-row-cost-${row.rowNumber}`,
                )}
            >
                {moneyOf(row.costTetri)}
            </span>
        ),
    },
    {
        id: IMPORT_TABLE_COLUMN.salePrice,
        header: t('catalogImport.products.columns.salePrice'),
        align: 'end',
        width: 100,
        minWidth: 100,
        cell: (row) => (
            <span
                className={textClass(row, 'text-sm tabular-nums')}
                {...getNodeTestIdProp(
                    `catalog-import-row-sale-price-${row.rowNumber}`,
                )}
            >
                {moneyOf(row.salePriceTetri)}
            </span>
        ),
    },
    {
        id: IMPORT_TABLE_COLUMN.match,
        header: t('catalogImport.products.columns.match'),
        width: 160,
        minWidth: 160,
        cell: (row) =>
            row.match === null ? null : (
                <MatchChip match={row.match} rowNumber={row.rowNumber} />
            ),
    },
    {
        id: IMPORT_TABLE_COLUMN.note,
        header: t('catalogImport.products.columns.note'),
        width: '1.4fr',
        minWidth: IMPORT_TABLE_NOTE_MIN_WIDTH_PX,
        cell: (row) =>
            row.note === null ? null : (
                <NoteCell
                    isMuted={row.isExcluded}
                    note={row.note}
                    onFix={isFlaggedRow(row) ? onEditRow : undefined}
                    rowNumber={row.rowNumber}
                />
            ),
    },
    {
        id: IMPORT_TABLE_COLUMN.actions,
        header: (
            <span className="sr-only">
                {t('catalogImport.products.columns.actions')}
            </span>
        ),
        align: 'end',
        width: IMPORT_TABLE_ACTIONS_WIDTH_PX,
        minWidth: IMPORT_TABLE_ACTIONS_WIDTH_PX,
        cell: (row) => (
            <span className="flex items-center gap-1">
                <RowEditButton
                    onEditRow={onEditRow}
                    rowNumber={row.rowNumber}
                />
                <RowExclusionButton
                    isExcluded={row.isExcluded}
                    onExcludeRow={onExcludeRow}
                    onIncludeRow={onIncludeRow}
                    rowNumber={row.rowNumber}
                />
            </span>
        ),
    },
];
